import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { trackPartnerClick } from "../lib/clickTrack";
import PriceTiers from "../components/PriceTiers";
import FeaturedCarousel from "../components/FeaturedCarousel";
import SpacesMap from "../components/SpacesMap";
import { FeaturedBadge, useFeaturedImpression, logFeaturedClick } from "../components/FeaturedBadge";
import Img from "../components/Img";
import WorkspaceFallbackTile, { needsFallback } from "../components/WorkspaceFallbackTile";
import SaveSpaceButton from "../components/SaveSpaceButton";
import ZipPromptBanner from "../components/ZipPromptBanner";

const AMENITY_LABELS = { wifi: "WiFi", standingDesk: "Standing Desk", phoneBooth: "Phone Booth", parking: "Parking" };

// Session 59.5 · P0 · Module-scope constant. Was previously declared inside
// the component body which meant it was recreated every render, tripping
// `react-hooks/exhaustive-deps` on the `types` useMemo. Moving it out of
// the render tree makes the dependency stable and lint-clean.
const REQUIRED_TYPES = ["Coworking Space", "Event Venue", "Meeting Room", "Private Office"];

function SpaceCard({ space: s, index }) {
  const impressionRef = useFeaturedImpression({ workspace: s, source: "spaces_grid" });
  const key = s.slug || s.workspace_id || `idx-${index}`;
  const testid = s.slug ? `space-card-${s.slug}` : `space-card-${s.workspace_id || index}`;
  const isExternal = Boolean(s.external_booking && s.booking_url);
  const isFeatured = Boolean(s.featured?.is_featured);
  const cardBorderStyle = isFeatured
    ? { border: "2px solid var(--gold)", boxShadow: "0 4px 18px rgba(184,145,58,0.18)" }
    : {};

  if (isExternal) {
    const stableSpaceId = s.slug || s.workspace_id || `idx-${index}`;
    return (
      <div style={{ position: "relative" }}>
        <a
          ref={impressionRef}
          href={s.booking_url}
          onClick={() => {
            logFeaturedClick({ workspace: s, source: "spaces_grid" });
            trackPartnerClick({ slug: s.slug, name: s.name, city: s.city, source: "spaces", fallbackUrl: s.booking_url });
          }}
          target="_blank"
          rel="noopener noreferrer"
          className="m-card spaces-link"
          data-testid={`space-partner-${s.slug}`}
          style={{ textDecoration: "none", color: "inherit", position: "relative", ...cardBorderStyle }}
        >
          {isFeatured && (
            <div style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
              <FeaturedBadge workspace={s} size="sm" />
            </div>
          )}
          <div className="m-card-img">
            {needsFallback(s) ? (
              <WorkspaceFallbackTile
                name={s.name}
                location={[s.neighborhood, s.city].filter(Boolean).join(" · ")}
                externalBooking={Boolean(s.external_booking)}
              />
            ) : (
              <Img src={s.img} alt={s.name} />
            )}
          </div>
          <div className="m-card-body">
            <div className="m-card-name">{s.name}</div>
            <div className="m-card-meta">{s.neighborhood} · {s.city}</div>
            <div className="m-card-type">{s.type}</div>
            {typeof s.distance_miles === "number" && (
              <div
                className="m-card-distance"
                data-testid={`space-distance-${s.slug || s.workspace_id || index}`}
                style={{
                  marginTop: 6, fontSize: 12,
                  color: "var(--gold-light, #d4a84b)",
                  letterSpacing: "0.04em",
                }}>
                📍 {s.distance_miles < 0.1 ? "Less than 0.1" : s.distance_miles.toFixed(1)} miles away
              </div>
            )}
            <span className="m-btn-gold" style={{ textAlign: "center", display: "block", marginTop: 14 }}>Book direct →</span>
          </div>
        </a>
        <div style={{ position: "absolute", top: 12, right: 12, zIndex: 3 }}>
          <SaveSpaceButton
            spaceType="workspace"
            spaceId={stableSpaceId}
            spaceName={s.name}
            variant="icon-only"
          />
        </div>
      </div>
    );
  }

  const linkTo = s.slug ? `/spaces/${s.slug}` : "/login";
  const stableSpaceId = s.slug || s.workspace_id || `idx-${index}`;
  return (
    <div style={{ position: "relative" }}>
      <Link
        ref={impressionRef}
        key={key}
        to={linkTo}
        className="m-card spaces-link"
        data-testid={testid}
        onClick={() => logFeaturedClick({ workspace: s, source: "spaces_grid" })}
        style={{ textDecoration: "none", color: "inherit", position: "relative", ...cardBorderStyle }}
      >
        {isFeatured && (
          <div style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
            <FeaturedBadge workspace={s} size="sm" />
          </div>
        )}
        <div className="m-card-img"><Img src={s.img} alt={s.name} /></div>
        <div className="m-card-body">
          {s.today && <span className="m-badge-today">Available today</span>}
          {s.bookable && <span className="m-badge-today" style={{ color: "var(--gold-light)" }}>· Instant book</span>}
          <div className="m-card-name">{s.name}</div>
          <div className="m-card-meta">{s.neighborhood} · {s.city}</div>
          <div className="m-card-type">{s.type}</div>
          {typeof s.distance_miles === "number" && (
            <div
              className="m-card-distance"
              data-testid={`space-distance-${s.slug || s.workspace_id || index}`}
              style={{
                marginTop: 6, fontSize: 12,
                color: "var(--gold-light, #d4a84b)",
                letterSpacing: "0.04em",
              }}>
              📍 {s.distance_miles < 0.1 ? "Less than 0.1" : s.distance_miles.toFixed(1)} miles away
            </div>
          )}
          <PriceTiers workspace={s} />
          {s.bookable ? (
            <span className="m-btn-gold" style={{ textAlign: "center", display: "block", marginTop: 14 }}>Book Now</span>
          ) : (
            <span className="m-btn-ghost" style={{ textAlign: "center", display: "block", marginTop: 14 }}>View details</span>
          )}
        </div>
      </Link>
      <div style={{ position: "absolute", top: 12, right: 12, zIndex: 3 }}>
        <SaveSpaceButton
          spaceType="workspace"
          spaceId={stableSpaceId}
          spaceName={s.name}
          variant="icon-only"
        />
      </div>
    </div>
  );
}

function PublicHeader() {
  return (
    <header className="rec-topbar">
      <Link to="/spaces" className="rec-brand" data-testid="spaces-brand">
        <span className="rec-brand-mark"><img src="/brand/metier-icon.png" alt="Métier" /></span>
      </Link>
      <nav className="rec-topnav">
        <Link to="/spaces" data-testid="nav-spaces">Spaces</Link>
        <Link to="/list-your-space" data-testid="nav-list-space">List your space</Link>
        <Link to="/login" data-testid="spaces-signin">Member sign in</Link>
      </nav>
    </header>
  );
}

export default function SpacesDirectory() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [type, setType] = useState("all");
  // Session 52 · Location Search — ZIP + radius filters.
  const [zip, setZip] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("");
  const [geo, setGeo] = useState(null); // { lat, lng }
  const [geoLoading, setGeoLoading] = useState(false);
  const [profSummary, setProfSummary] = useState(null);
  // Session 59.0 · P0 · Friendly error surfaced when a ZIP fails to geocode
  // (e.g. user typed "00000"). Backend returns 400 invalid_zip + copy.
  const [zipError, setZipError] = useState(null);
  // Session 42 · Set of `${entity_type}:${entity_id}` and slug keys for every
  // currently-featured item. Cards matching any key are hidden from the main
  // grid so a venue/workspace never renders twice on the page. Populated
  // once at mount via `/api/merchandising` (same endpoint the carousel uses).
  const [featuredKeys, setFeaturedKeys] = useState(() => new Set());
  // Session 51.2 · Map defaults collapsed so the first paint is a fast list
  // of cards. Users click to reveal the map.
  const [mapOpen, setMapOpen] = useState(false);

  // Session 58.9 · P0 · Unified search contract. Every filter — including
  // free-text `q` and `workspace_type` — is sent to the server so the
  // backend is the single source of truth. Client-side re-filter (below)
  // is a defensive fallback but should be idempotent given identical query.
  useEffect(() => {
    const params = new URLSearchParams();
    if (q && q.trim()) params.set("q", q.trim());
    if (zip) params.set("postal_code", zip);
    if (city !== "all") params.set("state", city); // dropdown filter is state
    if (type !== "all") params.set("workspace_type", type);
    if (radiusMiles) {
      // Session 58.9 · P0 · Send radius even without an explicit lat/lng.
      // The backend resolves the ZIP centroid via pgeocode when only
      // `postal_code` is present, so ZIP+radius must always apply the
      // 25-mile circle. If the user grants geolocation we prefer their
      // position; otherwise the ZIP is the anchor.
      params.set("radius_miles", String(radiusMiles));
      if (geo?.lat != null && geo?.lng != null) {
        params.set("lat", String(geo.lat));
        params.set("lng", String(geo.lng));
      }
    }
    const qs = params.toString();
    setLoading(true);
    setZipError(null);
    const t = setTimeout(() => {
      api.get(qs ? `/spaces?${qs}` : "/spaces")
        .then(r => setList(r.data))
        .catch((err) => {
          const detail = err?.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.error_code === "invalid_zip") {
            setZipError(detail.message || "We couldn't locate that ZIP code. Please check it and try again.");
            setList([]);
          }
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, zip, city, type, radiusMiles, geo?.lat, geo?.lng]);

  useEffect(() => {
    api.get("/locations").then(r => setLocations(r.data?.locations || [])).catch(() => {});
    // Build the featured-dedup set from the same endpoint the carousel uses.
    // Includes both categories (featured + editor's pick) — every promoted
    // item is excluded from the grid.
    api.get("/merchandising").then(r => {
      const keys = new Set();
      const acc = (item) => {
        if (!item) return;
        const eid = item.entity_id;
        const et = item.entity_type;
        if (eid) keys.add(`${et}:${eid}`);
        // Slug fallback — /api/spaces returns `slug` not the raw location_id
        // for venues, so match by slug too. Both venue and workspace slugs
        // live in the same namespace on that endpoint.
        const slug = item?.entity?.slug;
        if (slug) keys.add(`slug:${slug}`);
      };
      (r.data?.featured || []).forEach(acc);
      (r.data?.editors_pick || []).forEach(acc);
      setFeaturedKeys(keys);
    }).catch(() => setFeaturedKeys(new Set()));
    // Anonymous visitors get a "Who's on Métier" profession breakdown so
    // they see the network's professional mix before signing up.
    if (!user) {
      api.get("/members/profession-summary").then(r => setProfSummary(r.data)).catch(() => {});
    }
  }, [user]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not available in this browser");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        if (!radiusMiles) setRadiusMiles("25");
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        alert(`Could not get your location: ${err.message}`);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  // Data-driven location filter (Session 29): normalizes state abbrev + name
  // + DC variants so a workspace with `city: "Massachusetts"` and one with
  // `state: "MA"` both filter under a single MA entry.
  const STATE_TO_ABBR = {
    "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA","colorado":"CO",
    "connecticut":"CT","delaware":"DE","florida":"FL","georgia":"GA","hawaii":"HI","idaho":"ID",
    "illinois":"IL","indiana":"IN","iowa":"IA","kansas":"KS","kentucky":"KY","louisiana":"LA",
    "maine":"ME","maryland":"MD","massachusetts":"MA","michigan":"MI","minnesota":"MN",
    "mississippi":"MS","missouri":"MO","montana":"MT","nebraska":"NE","nevada":"NV",
    "new hampshire":"NH","new jersey":"NJ","new mexico":"NM","new york":"NY","north carolina":"NC",
    "north dakota":"ND","ohio":"OH","oklahoma":"OK","oregon":"OR","pennsylvania":"PA",
    "rhode island":"RI","south carolina":"SC","south dakota":"SD","tennessee":"TN","texas":"TX",
    "utah":"UT","vermont":"VT","virginia":"VA","washington":"WA","west virginia":"WV",
    "wisconsin":"WI","wyoming":"WY",
  };
  const DC_ALIASES = ["dc","d.c.","washington dc","washington, dc","washington d.c.","washington, d.c.","district of columbia"];
  const ABBR_SET = new Set(Object.values(STATE_TO_ABBR));
  const normalizeToAbbr = (...candidates) => {
    for (const raw of candidates) {
      if (!raw) continue;
      const s = String(raw).trim();
      if (!s) continue;
      const norm = s.toLowerCase().replace(/\./g,"").replace(/,/g,"").trim();
      if (DC_ALIASES.includes(norm) || norm === "dc") return "DC";
      const up = s.toUpperCase().trim();
      if (ABBR_SET.has(up)) return up;
      if (STATE_TO_ABBR[s.toLowerCase()]) return STATE_TO_ABBR[s.toLowerCase()];
      // "Silver Spring, MD" / "Boston MA" — scan for a trailing 2-letter code
      const tokens = s.split(/[\s,]+/).filter(Boolean);
      const last = (tokens[tokens.length - 1] || "").toUpperCase();
      if (ABBR_SET.has(last)) return last;
    }
    return null;
  };
  const spaceLocation = (s) => normalizeToAbbr(s.state, s.city, s.neighborhood);

  const types = useMemo(() => {
    const fromData = new Set(list.map(s => s.type));
    REQUIRED_TYPES.forEach(t => fromData.add(t));
    return Array.from(fromData).sort();
  }, [list]);



  // Session 58.9 · P0 · Featured dedup is honored ONLY on the unfiltered
  // baseline view. As soon as the user applies ANY filter (search text,
  // state, ZIP, radius, or type) we surface every match — otherwise a user
  // searching for "Kapow" would see zero cards because it's promoted to
  // the Featured carousel. Filters must never silently hide matches.
  const anyFilterActive = Boolean(q || (city && city !== "all") || (type && type !== "all") || zip || radiusMiles);
  const filtered = list.filter(s => {
    if (!anyFilterActive && featuredKeys.size) {
      const wsKey = s.workspace_id ? `workspace:${s.workspace_id}` : null;
      const vnKey = s.location_id || s.venue_id
        ? `venue:${s.location_id || s.venue_id}` : null;
      const slugKey = s.slug ? `slug:${s.slug}` : null;
      if ((wsKey && featuredKeys.has(wsKey))
          || (vnKey && featuredKeys.has(vnKey))
          || (slugKey && featuredKeys.has(slugKey))) {
        return false;
      }
    }
    if (city !== "all" && spaceLocation(s) !== city) return false;
    // Session 58.9 · P0 · Case-insensitive type normalization. Previous
    // exact-match filter dropped every card whose stored type differed by
    // even a single character ("Meeting Space" vs. "Meeting Room").
    if (type !== "all") {
      const norm = (v) => String(v || "").toLowerCase().trim();
      if (norm(s.type) !== norm(type) && norm(s.workspace_type) !== norm(type)) return false;
    }
    if (q) {
      // Session 58.9 · P0 · CASE-FIX. Previous version applied .toLowerCase()
      // only to the second concatenated string, leaving `name`, `city`, and
      // `state` in mixed case. Typing "kapow" would silently miss "Kapow
      // Buddy" and return zero. This is the primary customer-reported
      // "search is broken" root cause. Fix: lowercase the ENTIRE haystack.
      const hay = [
        s.name, s.neighborhood, s.city, s.state, s.postal_code,
        s.searchable_location_text, s.workspace_type, s.type,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase().trim())) return false;
    }
    return true;
  });

  // Session 42 · When the user is signed in, wrap the directory in AppShell
  // so member nav (Community, Hours, Events, Host, Admin dropdown) is
  // available. Anonymous visitors continue to see the marketing hero + the
  // public header (Spaces / List your space / Member sign in).
  const content = (
    <main className="rec-main">
        <span className="rec-eyebrow">MÉTIER · WORKSPACE DIRECTORY</span>
        <h1 className="rec-h1" data-testid="spaces-h1">
          Find a better place to work <em>wherever work takes you</em>.
        </h1>
        <p className="m-lede" style={{maxWidth:640}}>
          Explore a curated network of coworking spaces, private offices, day passes, meeting rooms, and professional venues. Join Métier for free.
        </p>

        {!user && (
          <div className="spaces-hero-ctas" style={{display:"flex", gap:12, flexWrap:"wrap", marginTop:22}}>
            <a href="#directory" className="m-btn-gold" data-testid="spaces-cta-explore">Explore workspaces</a>
            <Link to="/signup" className="m-btn-ghost" data-testid="spaces-cta-join">Join free</Link>
          </div>
        )}

        <div id="directory" className="m-filter-bar" data-testid="spaces-filter-bar"
             style={{marginTop:36, display:"grid", gap:12, gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))"}}>
          <div className="m-fg">
            <label>Search</label>
            <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Name, city, ZIP…" data-testid="spaces-search"
              style={{width:"100%",background:"var(--navy-mid)",color:"#fff",border:"1px solid rgba(212,168,75,0.22)",borderRadius:9,padding:"12px 13px",fontSize:14}}/>
          </div>
          <div className="m-fg">
            <label>State</label>
            <select value={city} onChange={e => setCity(e.target.value)} data-testid="spaces-filter-city">
              <option value="all">All states</option>
              {locations.map(loc => (
                <option key={loc.value} value={loc.value}>
                  {/* Session 59.2 · P0 · Show live count so the venue→workspace
                       cascade is visibly correct. Value should always equal
                       the /api/spaces?state=X result count. */}
                  {loc.label}{typeof loc.workspace_count === "number" ? ` (${loc.workspace_count})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="m-fg">
            <label>Near ZIP</label>
            <input type="text" inputMode="numeric" maxLength={5} value={zip}
              onChange={e => { setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5)); setZipError(null); }}
              placeholder="e.g. 03110" data-testid="spaces-filter-zip"
              style={{width:"100%",background:"var(--navy-mid)",color:"#fff",border:"1px solid rgba(212,168,75,0.22)",borderRadius:9,padding:"12px 13px",fontSize:14}}/>
            {zip && !zipError && (
              <div style={{marginTop:6, fontSize:11, color:"rgba(255,255,255,0.55)"}}>Sorted nearest first</div>
            )}
          </div>
          <div className="m-fg">
            <label>Radius</label>
            <select value={radiusMiles} onChange={e => setRadiusMiles(e.target.value)} data-testid="spaces-filter-radius">
              <option value="">Any distance</option>
              <option value="5">Within 5 miles</option>
              <option value="10">Within 10 miles</option>
              <option value="25">Within 25 miles</option>
              <option value="50">Within 50 miles</option>
            </select>
            {!geo && (
              <button type="button" onClick={useMyLocation}
                data-testid="spaces-locate-me"
                aria-label="Use my location"
                style={{marginTop:6,fontSize:11,color:"var(--gold)",background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left"}}>
                {geoLoading ? "Locating…" : "📍 Use my location"}
              </button>
            )}
            {geo && (
              <div style={{marginTop:6,fontSize:11,color:"rgba(255,255,255,0.55)"}}>
                📍 {geo.lat.toFixed(3)}, {geo.lng.toFixed(3)}
                <button type="button" onClick={() => setGeo(null)} data-testid="spaces-clear-my-location"
                  style={{marginLeft:8,background:"none",border:"none",color:"var(--gold)",cursor:"pointer",fontSize:11}}>clear</button>
              </div>
            )}
          </div>
          <div className="m-fg">
            <label>Space type</label>
            <select value={type} onChange={e => setType(e.target.value)} data-testid="spaces-filter-type">
              <option value="all">Any type</option>
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {zipError && (
          <div
            data-testid="spaces-zip-error"
            style={{
              marginTop: 12, padding: "10px 14px",
              background: "rgba(220, 80, 80, 0.10)",
              border: "1px solid rgba(220, 80, 80, 0.35)",
              borderRadius: 10, color: "#f8d5d5", fontSize: 13,
            }}>
            {zipError}
          </div>
        )}

        <div className="spaces-count-row" style={{marginTop:16, display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:14, flexWrap:"wrap"}}>
          <span data-testid="spaces-count" style={{fontFamily:"'Cormorant Garamond', serif", fontSize:22, color:"var(--gold-light, #d4a84b)"}}>
            {filtered.length} space{filtered.length === 1 ? "" : "s"} available
          </span>
          {(q || city !== "all" || type !== "all" || zip || radiusMiles) && (
            <button onClick={() => { setQ(""); setCity("all"); setType("all"); setZip(""); setRadiusMiles(""); setGeo(null); }}
              data-testid="spaces-clear-filters"
              style={{background:"none", border:"none", color:"rgba(255,255,255,0.55)", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer"}}>
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="m-loading-inline">Loading spaces…</div>
        ) : filtered.length === 0 ? (
          <div className="m-empty" data-testid="spaces-empty">No spaces match these filters yet. Adjust to see more.</div>
        ) : (
          <>
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              gap:12, padding:"10px 14px", marginBottom: mapOpen ? 8 : 12,
              background:"rgba(184,145,58,0.06)", border:"1px solid rgba(184,145,58,0.22)",
              borderRadius:12,
            }} data-testid="spaces-map-toggle-row">
              <div style={{display:"flex", gap:10, alignItems:"baseline"}}>
                <span style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Map view</span>
                <span style={{fontSize:12, color:"rgba(27,35,64,0.6)"}}>
                  {filtered.length} space{filtered.length === 1 ? "" : "s"} · {mapOpen ? "hide the map to focus on the list" : "click to see them all on a map"}
                </span>
              </div>
              <button type="button" onClick={() => setMapOpen(v => !v)}
                data-testid="spaces-map-toggle"
                style={{padding:"7px 16px", background: mapOpen ? "transparent" : "#1B2340", color: mapOpen ? "#1B2340" : "#FAF8F3", border: mapOpen ? "1px solid rgba(27,35,64,0.35)" : "none", borderRadius:999, fontSize:12, cursor:"pointer", fontWeight:500}}>
                {mapOpen ? "Hide map" : "Show map"}
              </button>
            </div>
            {mapOpen && (
              <SpacesMap spaces={filtered} radiusMiles={radiusMiles} geo={geo} featuredKeys={featuredKeys} />
            )}
            {!anyFilterActive && <FeaturedCarousel source="carousel_spaces" />}
            <div className="m-grid-3" data-testid="spaces-grid" style={{ marginTop: 8 }}>
              {filtered.map((s, i) => <SpaceCard key={s.slug || s.workspace_id || i} space={s} index={i} />)}
            </div>
          </>
        )}
      </main>
  );

  if (user) {
    return <AppShell>{content}</AppShell>;
  }

  return (
    <div className="rec-page">
      <PublicHeader />
      {content}
      <footer className="rec-footer">
        <span>© Métier · workmetier.app · <Link to="/llm-info" style={{color:"var(--m-muted)",textDecoration:"none"}} data-testid="footer-llm-info">For AI assistants</Link></span>
        <span>Are you a workspace owner? <Link to="/list-your-space" style={{color:"var(--gold)"}}>List your space →</Link></span>
      </footer>
    </div>
  );
}
